import React, { useEffect, useRef, useContext, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  StackedBarChart,
  PieChart,
  LineChart,
  BarChart,
  Tachometer
} from './graph-lib';
import { ApiBaseUrlContext } from '../App';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './Graph.css';  // ✅ New CSS file

const Graph = () => {
  const stackedAllRef = useRef(null);
  const stackedRangeRef = useRef(null);
  const pieRef = useRef(null);
  const lineRef = useRef(null);
  const operatorBarRef = useRef(null);
  const tachometerRefs = useRef({});
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t } = useTranslation();

  // State to hold the chart data
  const [stackedBarDataAll, setStackedBarDataAll] = useState(null);
  const [stackedBarDataRange, setStackedBarDataRange] = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [lineChartData, setLineChartData] = useState(null);
  const [operatorBarData, setOperatorBarData] = useState(null);
  const [tachometerData, setTachometerData] = useState([]);
  
  

  // 🗓️ Date Range State
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days back
  const [endDate, setEndDate] = useState(new Date());
  const [opStartDate, setOpStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days back
  const [opEndDate, setOpEndDate] = useState(new Date());
  const [showOperatorName, setShowOperatorName] = useState(true);

  // === 📝 API Call for the All Machines Stacked Bar Chart ===
  const fetchAverageFaultTimings = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/fault-errors/average-timings`);
      setTachometerData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch average fault timings:', error);
    }
  };
  const fetchAllMachineCosts = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/costs/all`);
      const machines = response.data.machines;

      // === 🎯 Transform Data for StackedBarChart
      const labels = machines.map(machine => `${machine.machineName} (${machine.machineid})`);

      const entities = [
        { label: t('graph.serviceTimeCost'), color: "#1ec401" },
        { label: t('graph.serviceMaterialCost'), color: "#39a4ff" },
        { label: t('graph.maintenanceTimeCost'), color: "#9d9d9d" },
        { label: t('graph.maintenanceMaterialCost'), color: "#e6a400" }
      ];

      const series = [
        {
          entity: 0,
          data: machines.map(machine => machine.serviceReportTimeCost)
        },
        {
          entity: 1,
          data: machines.map(machine => machine.serviceReportMaterialCost)
        },
        {
          entity: 2,
          data: machines.map(machine => machine.maintenanceTimeCost)
        },
        {
          entity: 3,
          data: machines.map(machine => machine.maintenanceMaterialCost)
        }
      ];

      // Final structure for the StackedBarChart
      setStackedBarDataAll({
        labels,
        entities,
        series
      });

    } catch (error) {
      console.error('Failed to fetch all machine cost data:', error);
    }
  };


  const fetchOperatorFaultCounts = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/fault-errors/per-operator`, {
        params: {
          startDate: opStartDate.getTime(),
          endDate: opEndDate.getTime()
        }
      });

      const rawLabels = response.data.labels; // Array of { operatorId, operatorName }
      const labelStrings = rawLabels.map(op => {
        const id = (op.operatorId === null || op.operatorId === 'null') ? null : op.operatorId;
        const name = op.operatorName?.trim();
      
        if (id === null) {
          return t('graph.noUserAssigned');
        } else if (showOperatorName) {
          return name ? `${name} (ID: ${id})` : `${t('graph.unknownUser')} (ID: ${id})`;
        } else {
          return id;
        }
      });

      setOperatorBarData({
        labels: labelStrings,
        entities: [
          { label: t('graph.lowPriority'), color: "#1ec401" },
          { label: t('graph.mediumPriority'), color: "#e6a400" },
          { label: t('graph.highPriority'), color: "#d81e05" }
        ],
        series: response.data.series
      });

    } catch (error) {
      console.error('Failed to fetch operator fault counts:', error);
    }
  };

  
  // === 📝 API Call for Priority Distribution Pie Chart ===
  const fetchMachinePriorityDistribution = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/priority`);
      const machines = response.data;

      // ✅ Count priorities
      const priorityCount = {
        Low: 0,
        Medium: 0,
        High: 0
      };

      machines.forEach(machine => {
        if (priorityCount[machine.priority] !== undefined) {
          priorityCount[machine.priority]++;
        }
      });

      const total = machines.length;

      // ✅ Prepare Pie Data
      setPieChartData({
        labels: [t('graph.low'), t('graph.medium'), t('graph.high')],
        entities: [
          { label: t('graph.low'), color: "#1ec401" },
          { label: t('graph.medium'), color: "#e6a400" },
          { label: t('graph.high'), color: "#d81e05" }
        ],
        series: [
          { entity: 0, data: [(priorityCount.Low / total) * 100] },
          { entity: 1, data: [(priorityCount.Medium / total) * 100] },
          { entity: 2, data: [(priorityCount.High / total) * 100] }
        ]
      });

    } catch (error) {
      console.error('Failed to fetch machine priority data:', error);
    }
  };

  // === 📝 API Call for Date Range Machine Costs ===
  const fetchMachineCostsByRange = async () => {
    if (!startDate || !endDate) {
      alert(t('graph.selectStartEndDates'));
      return;
    }

    try {
      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();

      const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/costs/range`, {
        params: {
          startDate: startTimestamp,
          endDate: endTimestamp
        }
      });

      const machines = response.data.machines;

      const labels = machines.map(machine => `${machine.machineName} (${machine.machineid})`);

      const entities = [
        { label: t('graph.serviceTimeCost'), color: "#1ec401" },
        { label: t('graph.serviceMaterialCost'), color: "#39a4ff" }
      ];

      const series = [
        {
          entity: 0,
          data: machines.map(machine => machine.serviceReportTimeCost)
        },
        {
          entity: 1,
          data: machines.map(machine => machine.serviceReportMaterialCost)
        }
      ];

      setStackedBarDataRange({
        labels,
        entities,
        series
      });

    } catch (error) {
      console.error('Failed to fetch machine cost data by date range:', error);
    }
  };

  // === 📝 API Call for Weekly Average Time (Line Chart) ===
  const fetchWeeklyAverageTime = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/faults/average-time`);
      const data = response.data.data;

      // 🎯 Transform Data for Line Chart
      const labels = data.map(weekData => t('graph.week', { week: weekData.week }));
      const series = [
        {
          label: t('graph.averageTimeHrs'),
          color: "#1ec401",
          data: data.map(weekData => weekData.averageTime)
        }
      ];

      setLineChartData({
        labels,
        series
      });
    } catch (error) {
      console.error('Failed to fetch weekly average time:', error);
    }
  };

  // 🔄 Load All Data on Mount
  useEffect(() => {
    fetchAllMachineCosts();
    fetchMachinePriorityDistribution();
    fetchWeeklyAverageTime();
    fetchAverageFaultTimings();
    fetchMachineCostsByRange();
    fetchOperatorFaultCounts();
  }, [apiBaseUrl]);

  // === 📊 Render Charts ===
// === 📊 Render Charts ===
useEffect(() => {
  [stackedAllRef, stackedRangeRef, pieRef, lineRef, operatorBarRef].forEach(ref => {
    if (ref.current) ref.current.innerHTML = '';
  });

  if (stackedBarDataAll) {
    const stackedAll = new StackedBarChart(stackedAllRef.current, {
      title: t('graph.totalCostsForAllMachines'),
      resizable: false
    });
    stackedAll.setData(stackedBarDataAll);
  }

  if (stackedBarDataRange) {
    const stackedRange = new StackedBarChart(stackedRangeRef.current, {
      title: t('graph.machineCostDistributionDateRange'),
      resizable: false
    });
    stackedRange.setData(stackedBarDataRange);
  }

  if (pieChartData) {
    const pie = new PieChart(pieRef.current, {
      ttitle: t('graph.machinePriorityDistribution'),
      resizable: false
    });
    pie.setData(pieChartData);
  }

  if (lineChartData) {
    const line = new LineChart(lineRef.current, {
      title: t('graph.averageTimeFromCreatedToStarted'),
      resizable: false
    });
    line.setData(lineChartData);
  }

  if (operatorBarData) {
    const operatorBar = new BarChart(operatorBarRef.current, {
      title: t('graph.faultsReportedPerOperator'),
      resizable: false
    });
    operatorBar.setData(operatorBarData);
  }
}, [
  stackedBarDataAll,
  stackedBarDataRange,
  pieChartData,
  lineChartData,
  operatorBarData // ✅ Add this here
]);
useEffect(() => {
  tachometerData
    .filter(machine =>
      machine.beforeStarted !== null &&
      machine.workedOn !== null &&
      machine.completed !== null
    )
    .forEach(machine => {
      const tachometerRef = tachometerRefs.current[`${machine.machineid}-${machine.priority}`];

      if (tachometerRef) {
        const tachometer = new Tachometer(tachometerRef, {
          title: t('graph.tachometerTitle', { machine: machine.machine, priority: machine.priority }),
          resizable: false
        });

        tachometer.setData({
          entities: [
            { label: t('graph.beforeStarted'), percent: 15, color: "#1ec401" },
            { label: t('graph.workedOn'), percent: 15, color: "#e6a400" },
            { label: t('graph.completed'), percent: 70, color: "#d81e05" }
          ],
          annotations: [
            { label: t('graph.reported'), percent: 0 },
            { label: t('graph.started'), percent: machine.beforeStarted },
            { label: t('graph.finished'), percent: 100 }
          ]
        });

        tachometer.setValue(machine.beforeStarted);
      }
    });
}, [tachometerData]);


return (
  <div className="App">
    <h1>{t('graph.chartGallery')}</h1>

    <section>
      <h2>{t('graph.machinePriorityDistribution')}</h2>
      <div ref={pieRef} className="grid-item" />
    </section>

    <section>
      <h2>{t('graph.totalCostsForAllMachines')}</h2>
      <div ref={stackedAllRef} className="grid-item" />
    </section>

    <section>
      <h2>{t('graph.machineCostDistributionDateRange')}</h2>
      <div className="date-picker-section">
        <DatePicker selected={startDate} onChange={setStartDate} />
        <DatePicker selected={endDate} onChange={setEndDate} />
        <button onClick={fetchMachineCostsByRange}>
          {t('graph.loadDateRangeData')}
        </button>
      </div>
      <div ref={stackedRangeRef} className="grid-item" />
    </section>

    <section>
      <h2>{t('graph.weeklyAverageTimeToStart')}</h2>
      <div ref={lineRef} className="grid-item" />
    </section>

    <section>
      <h2>{t('graph.faultsReportedPerOperator')}</h2>
      <div className="date-picker-section">
        <DatePicker selected={opStartDate} onChange={setOpStartDate} />
        <DatePicker selected={opEndDate} onChange={setOpEndDate} />
        <button onClick={fetchOperatorFaultCounts}>
          {t('graph.loadOperatorFaults')}
        </button>
        <div className="label-toggle-section">
          <label>
            <input
              type="radio"
              value="name"
              checked={showOperatorName}
              onChange={() => setShowOperatorName(true)}
            />
            {t('graph.showOperatorName')}
          </label>
          <label>
            <input
              type="radio"
              value="id"
              checked={!showOperatorName}
              onChange={() => setShowOperatorName(false)}
            />
            {t('graph.showOperatorId')}
          </label>
        </div>
      </div>
      <div ref={operatorBarRef} className="grid-item" />
    </section>

    <section>
      <h2>{t('graph.averageFaultTimings')}</h2>
      <div className="tachometer-container">
        {tachometerData
          .filter(machine =>
            machine.beforeStarted !== null &&
            machine.workedOn !== null &&
            machine.completed !== null
          )
          .map(machine => (
            <div
              key={`${machine.machineid}-${machine.priority}`} // Unique key per machine+priority
              ref={(el) => tachometerRefs.current[`${machine.machineid}-${machine.priority}`] = el}
              className="tachometer-box"
            />
          ))}
      </div>
    </section>
  </div>

);
}

export default Graph;
